import { gcm } from "@noble/ciphers/aes.js";
import { x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { concat, utf8 } from "../bytes";
import { HermesError } from "../errors";

/**
 * 手机 ⇄ connector 的端到端加密（与 connector/timibot-connector.py、HermesKit RelayCrypto.swift 逐字节一致，协议见 docs/RELAY.md）。
 *
 * 类 Noise-IK：从配对链接里拿到主机的静态公钥 Hs，主机按公钥白名单认手机。
 *   手机 → 主机  0x01 | Pe | AESGCM(k1, nonce 0, {v, ps, pair, device}, ad = 0x01‖Pe)      k1 = HKDF(DH(pe,Hs))
 *   主机 → 手机  0x02 | He | AESGCM(k_h2c, nonce 0, {ok, auth, name, …}, ad = 0x02‖Pe‖He)
 *   会话密钥 = HKDF(DH(pe,Hs) ‖ DH(pe,He) ‖ DH(ps,He), salt = SHA256(PROTO‖Pe‖He‖Hs‖Ps)) → k_c2h ‖ k_h2c
 *   之后每帧：0x10(完整) / 0x11(还有后续分片) | AESGCM(密钥, nonce = 0⁴‖计数器(大端 u64), ad = 类型字节)
 */
export const RelayFrame = {
  hello: 0x01,
  welcome: 0x02,
  reject: 0x03,
  final: 0x10,
  more: 0x11,
  /** 单帧明文上限：中转对单条 WebSocket 消息有大小限制 */
  fragment: 192 * 1024,
} as const;

const PROTO = utf8("timibot-relay-v1");

export function relayNonce(counter: number): Uint8Array {
  const n = new Uint8Array(12);
  new DataView(n.buffer).setBigUint64(4, BigInt(counter), false);
  return n;
}

function derive(ikm: Uint8Array, salt: Uint8Array, info: string, length: number): Uint8Array {
  return hkdf(sha256, ikm, salt, utf8(info), length);
}

export function generateClientKey(): Uint8Array {
  return x25519.utils.randomSecretKey();
}

export function publicKeyOf(secret: Uint8Array): Uint8Array {
  return x25519.getPublicKey(secret);
}

export class RelayHandshake {
  private readonly ephemeral: Uint8Array;
  private readonly ephemeralPublic: Uint8Array;
  private readonly dh1: Uint8Array;
  readonly clientPublicKey: Uint8Array;

  constructor(private readonly client: Uint8Array, private readonly hostKey: Uint8Array, ephemeral?: Uint8Array) {
    if (hostKey.length !== 32) throw HermesError.invalidConfiguration("配对信息里的主机公钥无效，请重新配对");
    this.ephemeral = ephemeral ?? x25519.utils.randomSecretKey();
    this.ephemeralPublic = x25519.getPublicKey(this.ephemeral);
    this.clientPublicKey = x25519.getPublicKey(client);
    try {
      this.dh1 = x25519.getSharedSecret(this.ephemeral, hostKey);
    } catch {
      throw HermesError.invalidConfiguration("配对信息里的主机公钥无效，请重新配对");
    }
  }

  hello(payload: Uint8Array): Uint8Array {
    const pe = this.ephemeralPublic;
    const k1 = derive(this.dh1, PROTO, "timibot hello", 32);
    const sealed = gcm(k1, relayNonce(0), concat(Uint8Array.of(RelayFrame.hello), pe)).encrypt(payload);
    return concat(Uint8Array.of(RelayFrame.hello), pe, sealed);
  }

  /** 解开主机的回复，得到会话密钥和欢迎信息。 */
  finish(frame: Uint8Array): { cipher: RelayCipher; welcome: Uint8Array } {
    if (frame.length < 1 + 32 + 16 || frame[0] !== RelayFrame.welcome) {
      throw HermesError.transport("主机的握手回复格式不对");
    }
    const pe = this.ephemeralPublic;
    const he = frame.slice(1, 33);
    let dh2: Uint8Array, dh3: Uint8Array;
    try {
      dh2 = x25519.getSharedSecret(this.ephemeral, he);
      dh3 = x25519.getSharedSecret(this.client, he);
    } catch {
      throw HermesError.transport("主机身份校验失败（公钥不匹配），请重新配对");
    }
    const salt = sha256(concat(PROTO, pe, he, this.hostKey, this.clientPublicKey));
    const okm = derive(concat(this.dh1, dh2, dh3), salt, "timibot session", 64);
    const c2h = okm.slice(0, 32);
    const h2c = okm.slice(32, 64);
    let welcome: Uint8Array;
    try {
      welcome = gcm(h2c, relayNonce(0), concat(Uint8Array.of(RelayFrame.welcome), pe, he)).decrypt(frame.slice(33));
    } catch {
      throw HermesError.transport("主机身份校验失败（公钥不匹配），请重新配对");
    }
    return { cipher: new RelayCipher(c2h, h2c, 0, 1), welcome };
  }
}

/** 握手后的对称加密状态。两个方向各自一个计数器，帧必须按顺序收发。 */
export class RelayCipher {
  constructor(
    private readonly sendKey: Uint8Array,
    private readonly recvKey: Uint8Array,
    private sendCounter: number,
    private recvCounter: number,
  ) {}

  /** 一条消息 → 一个或多个加密帧（大消息分片）。 */
  seal(message: Uint8Array): Uint8Array[] {
    const frames: Uint8Array[] = [];
    let offset = 0;
    do {
      const end = Math.min(offset + RelayFrame.fragment, message.length);
      const kind = end < message.length ? RelayFrame.more : RelayFrame.final;
      const sealed = gcm(this.sendKey, relayNonce(this.sendCounter), Uint8Array.of(kind)).encrypt(message.subarray(offset, end));
      this.sendCounter += 1;
      frames.push(concat(Uint8Array.of(kind), sealed));
      offset = end;
    } while (offset < message.length);
    return frames;
  }

  open(frame: Uint8Array): { kind: number; plain: Uint8Array } {
    if (frame.length < 17 || (frame[0] !== RelayFrame.final && frame[0] !== RelayFrame.more)) {
      throw HermesError.transport("收到无法识别的加密帧");
    }
    const kind = frame[0];
    const plain = gcm(this.recvKey, relayNonce(this.recvCounter), Uint8Array.of(kind)).decrypt(frame.subarray(1));
    this.recvCounter += 1;
    return { kind, plain };
  }
}
